import { useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2,
  MoreHorizontal,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlatformAdminOnly } from "@/components/portal/PermissionGate";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/dateFormat";
import { useAuth } from "@/contexts/AuthContext";
import { ragApi, type PromptTemplate, type PromptType } from "@/services/api/rag";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/knowledgebase-file/templates")({
  component: PromptTemplatesPage,
});

const PROMPT_TYPES: { value: PromptType; label: string }[] = [
  { value: "system", label: "System Prompt" },
  { value: "context", label: "Context Prompt" },
  { value: "enrichment", label: "Enrichment Prompt" },
  { value: "table_aware_response", label: "Table Aware Response" },
  // No "Query Intent Classification": query intent is decided by the Jev judge
  // (dp #2713), so that template is no longer read and the API refuses it.
];

function PromptTemplatesPage() {
  const { token } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<PromptTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Search & sort state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string>("updated_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [formData, setFormData] = useState<{
    prompt_type: PromptType;
    template: string;
    variables: Record<string, string>;
    is_active: boolean;
  }>({
    prompt_type: "system",
    template: "",
    variables: {},
    is_active: true,
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const getSortDirection = (column: string): "asc" | "desc" | null => {
    return sortBy === column ? sortOrder : null;
  };

  // Server-side pagination
  const fetchTemplates = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<PromptTemplate>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };
      const response = await ragApi.listPromptTemplates(token, {
        search: searchTerm || undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy as "updated_at" | "created_at" | "prompt_type" | "version" | "is_active",
        sort_order: sortOrder,
      });
      return {
        data: response.templates,
        totalCount: response.total,
        page,
        pageSize,
      };
    },
    [token, searchTerm, sortBy, sortOrder],
  );

  const {
    data: templates,
    isLoading,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<PromptTemplate>({
    fetchFn: fetchTemplates,
    dependencies: [token, searchTerm, sortBy, sortOrder],
  });

  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormData({
      prompt_type: "system",
      template: "",
      variables: {},
      is_active: true,
    });
    setShowDialog(true);
  };

  const handleEdit = (template: PromptTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      prompt_type: template.prompt_type,
      template: template.template,
      variables: template.variables,
      is_active: template.is_active,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    try {
      if (selectedTemplate) {
        await ragApi.updatePromptTemplate(token, selectedTemplate.id, {
          template: formData.template,
          variables: formData.variables,
        });
        toast.success("Template updated");
      } else {
        await ragApi.createPromptTemplate(token, formData);
        toast.success("Template created");
      }
      setShowDialog(false);
      refresh();
    } catch (error) {
      console.error("Failed to save template:", error);
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!token) return;
    if (!confirm("Are you sure you want to delete this template?")) return;

    try {
      await ragApi.deletePromptTemplate(token, templateId);
      toast.success("Template deleted");
      refresh();
    } catch (error) {
      console.error("Failed to delete template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleToggleActive = async (template: PromptTemplate) => {
    if (!token) return;
    try {
      if (template.is_active) {
        await ragApi.deactivatePromptTemplate(token, template.id);
        toast.success("Template deactivated");
      } else {
        await ragApi.activatePromptTemplate(token, template.id);
        toast.success("Template activated");
      }
      refresh();
    } catch (error) {
      console.error("Failed to toggle template:", error);
      toast.error("Failed to update template");
    }
  };

  const getPromptTypeLabel = (type: PromptType) => {
    return PROMPT_TYPES.find((t) => t.value === type)?.label || type;
  };

  return (
    <PlatformAdminOnly
      fallback={
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShieldAlert className="h-16 w-16 text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Access Restricted
          </h1>
          <p className="text-muted-foreground max-w-md">
            Prompt Templates configuration is only available to platform
            administrators.
          </p>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900">
              Prompt Templates
            </h1>
            <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
              Configure and manage prompt templates for query processing
            </p>
          </div>
          <Button className="rounded-full" onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>

      {/* Content card */}
      <div className="rounded-3xl border border-slate-100 bg-white p-3 md:p-6 shadow-sm space-y-4 md:space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search templates..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <Edit className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No templates match your search" : "No templates yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try adjusting your search."
                : "Create prompt templates to customise query behavior."}
            </p>
            {!searchTerm && (
              <Button onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("prompt_type")}
                  onSort={() => handleSort("prompt_type")}
                >
                  Type
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("is_active")}
                  onSort={() => handleSort("is_active")}
                >
                  Status
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("version")}
                  onSort={() => handleSort("version")}
                  className="hidden md:table-cell"
                >
                  Version
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="hidden md:table-cell"
                >
                  Default
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("updated_at")}
                  onSort={() => handleSort("updated_at")}
                  className="hidden lg:table-cell"
                >
                  Updated
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="w-[50px]"
                >
                  {" "}
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell className="font-medium">
                    {getPromptTypeLabel(template.prompt_type)}
                  </TableCell>
                  <TableCell>
                    {template.is_active ? (
                      <Badge variant="success">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <XCircle className="mr-1 h-3 w-3" />
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="outline">
                      v{template.version}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {template.is_default && (
                      <Badge variant="secondary">Default</Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {formatDateTime(template.updated_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleEdit(template)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleToggleActive(template)}
                        >
                          {template.is_active ? (
                            <>
                              <XCircle className="h-4 w-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        {!template.is_default && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(template.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {pagination.totalCount > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              pageSizeOptions={pageSizeOptions}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={goToPage}
              onPageSizeChange={setPageSize}
              onFirstPage={firstPage}
              onLastPage={lastPage}
              onPrevPage={prevPage}
              onNextPage={nextPage}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate
                ? "Update the prompt template configuration"
                : "Create a new prompt template for processing"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="prompt-type">Prompt Type</Label>
              <Select
                value={formData.prompt_type}
                onValueChange={(value: PromptType) =>
                  setFormData({ ...formData, prompt_type: value })
                }
                disabled={!!selectedTemplate}
              >
                <SelectTrigger id="prompt-type" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROMPT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="template">Template Content</Label>
              <Textarea
                id="template"
                value={formData.template}
                onChange={(e) =>
                  setFormData({ ...formData, template: e.target.value })
                }
                className="mt-1 h-64 font-mono text-sm"
                placeholder="Enter your prompt template here. Use {variable_name} for variables."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.template.trim()}
            >
              {saving ? "Saving..." : selectedTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </PlatformAdminOnly>
  );
}
